import React, { FC, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  ValueWrapper,
  TextWrapper,
} from "../../PromotionForm/Summary/Summary.style";
import { isDate } from "date-fns";
import { format } from "date-fns-tz";
import { clearApi } from "store/actions/api.actions";
import {
  BUNDLE_PROMOTION_UPDATE_STATUS,
  RESOURCE_GET_BY_ID,
  BULK_BUNDLE_RESOURCE_GET_BY_ID,
} from "store/store.types";
import {
  Alert,
  Box,
  Button,
  Input,
  Link,
  Text,
  VirtuazlizedList,
} from "common";
import { useTimeZoneSupport, useApiInfo } from "hooks";
import { downloadCSV } from "utils/app.utils";
import { Badge } from "custom";
import { IFieldType } from "store/reducers/bundlePromotion.reducers";
import { getPosInfoResource } from "store/actions/bundlePromotion.actions";
import { getBulkBundleResource } from "store/actions/bulkBundleUtility.actions";
import { getCollectionFields, flattenBundleCampaignDetails } from "./DetailsHelper";
interface IProps {
  formValues: any;
  error?: string;
  onEditBundleInfo?: () => void;
}

interface ValueBlockProps {
  name: string;
  value: string | number | string[] | number[] | boolean;
}

const ValueBlock: React.FC<ValueBlockProps> = ({ name, value }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const updatedList = useMemo(() => {
    if (Array.isArray(value)) {
      if (searchTerm) {
        return (value as (string | number)[]).filter((item) =>
          typeof item === "string" ? item.startsWith(searchTerm) : true
        );
      }
    }
    return Array.isArray(value) ? value : [];
  }, [value, searchTerm]);

  const isDownloadableField = [
    "Item List",
    "Enabled Pos",
    "Parent Sku List",
    "Free Gift Sku List",
  ].includes(name);

  return (
    <ValueWrapper>
      <Text
        fontWeight={600}
        whiteSpace="nowrap"
        fontSize={["14px", "16px", "16px", "16px"]}
      >
        {name}
      </Text>

      {Array.isArray(value) ? (
        <Box my="8px" gridGap="10px">
          {value.length > 5 && (
            <Input
              testId="summary-virtualizedlist-search"
              size="tiny"
              label="Search ..."
              variant="outlined"
              onChange={onChangeSearch}
              value={searchTerm}
            />
          )}
          {value.length > 5 ? (
            <VirtuazlizedList
              itemCount={updatedList.length}
              itemData={updatedList}
              itemSize={30}
              height={150}
              width={250}
              containerStyles={{
                backgroundColor: "#f7f7f9",
                height: 150,
                width: "100%",
              }}
            />
          ) : (
            value.map((single, index) => (
              <TextWrapper key={`Test-${name}-${index}`}>
                {String(single)}
              </TextWrapper>
            ))
          )}
          {isDownloadableField && Array.isArray(value) && (
            <Button
              variant="outlined"
              color="primary"
              round
              width="auto"
              data-testid={`downloadFile-${name}`}
              onClick={() =>
                downloadCSV(
                  value.map((v) => String(v)).join("\n"),
                  `${name}.csv`
                )
              }
            >
              Download
            </Button>
          )}
        </Box>
      ) : (
        <Box>
          <TextWrapper>{value}</TextWrapper>
        </Box>
      )}
    </ValueWrapper>
  );
};

const CampaignDetails: FC<IProps> = ({
  error,
  formValues,
  onEditBundleInfo,
}) => {
  const excludedNames = ["CampaignId", "Status", "StoreId", "Collections"];
  const fileFieldNames = ["ItemList", "BulkDocId", "ParentSkuList", "FreeGiftSkuList"];

  const { dateToTimeZone, timeZone } = useTimeZoneSupport();
  const dispatch = useDispatch();

  const { error: posError, isLoading } = useApiInfo(RESOURCE_GET_BY_ID);
  const { error: bulkError, isLoading: isBulkResouceLoading } = useApiInfo(
    BULK_BUNDLE_RESOURCE_GET_BY_ID
  );

  const downloadFileHandler = useCallback(
    (id: string) => {
      dispatch(getPosInfoResource(id));
    },
    [dispatch]
  );

  const oncloseNotification = () => {
    dispatch(clearApi(BUNDLE_PROMOTION_UPDATE_STATUS));
    dispatch(clearApi(RESOURCE_GET_BY_ID));
    dispatch(clearApi(BULK_BUNDLE_RESOURCE_GET_BY_ID));
  };

  const bundleCampaignDetails = useMemo(() => {
    const flattened = flattenBundleCampaignDetails(formValues).filter(
      ({ name, value }) => !excludedNames.includes(name) && value,
    );
    const collectionFields = getCollectionFields(
      formValues?.offer?.collections,
    );

    return [...flattened, ...collectionFields];
  }, [formValues]);

  const { fieldsWithoutFile, fileFields } = useMemo(() => {
    const fileFields: IFieldType[] = [];
    const fieldsWithoutFile: IFieldType[] = [];

    bundleCampaignDetails.forEach((field) => {
      if (fileFieldNames.includes(field.name)) {
        fileFields.push(field);
      } else {
        fieldsWithoutFile.push(field);
      }
    });

    return { fieldsWithoutFile, fileFields };
  }, [bundleCampaignDetails, fileFieldNames]);

  const renderCampaignInfo = (
    item: { name: string; value: any, op?: string },
    index: number
  ) => {
    let value;
    let name = item.name.replace(/([a-z])([A-Z])/g, "$1 $2");
    if (
      (name === "Start Date" || name === "End Date") &&
      isDate(new Date(item.value))
    ) {
      value = format(
        dateToTimeZone(item.value) as Date,
        "MMMM d','yyyy 'at' hh:mm:ss' 'aa XX",
        { timeZone }
      );
    } else if (name === "Pos Id") {
      name = "POS list with active promotion";
      value = (
        <Button
          variant="outlined"
          color="primary"
          round
          width="auto"
          disabled={isLoading}
          data-testid="download-pos-info"
          onClick={() => downloadFileHandler(item.value)}
        >
          Download
        </Button>
      );
    } else if (name === "Bulk Doc Id") {
      name = "Bulk File Name";
      value = (
        <Button
          variant="outlined"
          color="primary"
          round
          width="auto"
          disabled={isBulkResouceLoading}
          data-testid="download-bulk-upload"
          onClick={() => dispatch(getBulkBundleResource(item.value))}
        >
          Download
        </Button>
      );
    } else if(name === "Pos Info List"){
      const { op, value: itemValue } = item?.value?.[0] ?? {};
      name = `POS list with ${op?.toLocaleLowerCase() === "in" ? "active" : "excluded"} promotion`;
      value = (
        <Button
          variant="outlined"
          color="primary"
          round
          width="auto"
          disabled={isLoading}
          data-testid="download-pos-info-list"
          onClick={() => downloadFileHandler(itemValue)}
        >
          Download
        </Button>
      );
    } else {
      value = typeof item.value === "boolean" ? String(item.value) : item.value;
    }
    return <ValueBlock key={`${name}-${index}`} name={name} value={value} />;
  };

  return (
    <>
      <Box
        pb="15px"
        borderBottom="1px solid #E6E6E6"
        display="flex"
        flexDirection={["column", "row", "row", "row"]}
        justifyContent="space-between"
        alignItems={["flex-start", "flex-end", "flex-end", "flex-end"]}
        mb={2}
      >
        <Box>
          <Text
            fontSize="28px"
            marginBottom="10px"
            fontWeight={600}
            data-testid="bundle-details-header"
          >
            Bundle Summary
          </Text>
          <Text
            fontSize="16px"
            marginBottom="10px"
            data-testid="bundle-details-title"
          >
            Bundle Promotion details summary
          </Text>
        </Box>
        {formValues.status.toLowerCase() && (
          <Box mb="12px" flexDirection="row" display="flex" alignItems="center">
            <Badge
              as={formValues.status.toLowerCase()}
              height="26px"
              width="120px"
              borderRadius="5px"
            />
          </Box>
        )}
      </Box>
      {(error || posError || bulkError) && (
        <Alert
          padding="5px 15px"
          width="100%"
          variant="filled"
          severity="error"
          margin="15px 0"
          onClick={oncloseNotification}
        >
          {error || posError || bulkError}
        </Alert>
      )}
      <Box width="100%" data-testid="summary-container">
        <Box
          display="flex"
          justifyContent="space-between"
          my="10px"
          alignItems="center"
        >
          <Text fontSize="18px" fontWeight={700}>
            {fileFields.some((field) => field.name === "BulkDocId")
              ? "Bundle Information (Created By Bulk Utility)"
              : "Bundle Information"}
          </Text>
          {onEditBundleInfo && (
            <Link
              variant="default"
              color="primary"
              onClick={onEditBundleInfo}
              fontWeight={500}
              data-testid="edit-basic-button"
            >
              Edit
            </Link>
          )}
        </Box>
        <Box
          gridGap="15px"
          my="15px"
          width="100%"
          data-testid="basic-info-container"
          gridTemplateColumns={["1fr", "1fr", "1fr", "1fr 1fr"]}
        >
          {fieldsWithoutFile.map((item, index) =>
            renderCampaignInfo(item, index)
          )}
        </Box>
        <Box
          gridGap="15px"
          my="15px"
          width="100%"
          data-testid="basic-info-container"
          gridTemplateColumns={["1fr", "1fr", "1fr", "1fr 1fr"]}
        >
          {fileFields.map((item, index) => renderCampaignInfo(item, index))}
        </Box>
      </Box>
    </>
  );
};

export default CampaignDetails;
